// The in-chat "agent at work" card's pure rules, in their own module with NO runtime imports.
//
// Split out of the component deliberately: a jest test that imports `AgentAtWorkCard.tsx` would pull
// react-query and common-lib's `entities` barrel into the graph, and in CI that barrel resolves to source
// and drags the ESM-only `natural` through a CommonJS jest — the suite then fails to RUN (see the Tests
// section of this package's CLAUDE.md). Keeping the rules here is what makes them testable at all.

/** How many of the newest rows the card shows. A WINDOW on work in motion, not the transcript — the whole
 *  thing is one click away on the task. 1:1 with both phones' `windowSize` / `AGENT_AT_WORK_WINDOW`. */
export const AGENT_AT_WORK_WINDOW = 4;

export function agentAtWorkWindow<T>(rows: T[], limit: number = AGENT_AT_WORK_WINDOW): T[] {
  return limit > 0 && rows.length > limit ? rows.slice(rows.length - limit) : rows;
}

/** Half the flip — one edge-on rotation, in ms. 1:1 with native's 180. */
export const FLIP_HALF_MS = 180;

/**
 * Fold one live row into a side's list: patch the row carrying the same id IN PLACE, else append.
 *
 * The duplexer republishes a row as it is written, so the same id arrives several times with growing
 * content — dropping the repeat keeps the first, shortest version on screen until the next poll, which
 * is the one thing a card about work in motion must not do. Patching in place rather than re-sorting is
 * what keeps a row from jumping while it grows: its `createdDate` does not change, so it has already
 * earned its position. Only a genuinely new row needs ordering.
 *
 * 1:1 with iOS `SymphonyStore.upsertAgentConversationMessage` and Android's `upsert`.
 */
export function upsertAgentAtWorkRow<T extends { id?: string; createdDate?: string }>(rows: readonly T[], incoming: T): T[] {
  const index = incoming.id ? rows.findIndex((row) => row.id === incoming.id) : -1;
  if (index >= 0) {
    const next = [...rows];
    next[index] = incoming;
    return next;
  }
  return [...rows, incoming].sort((a, b) => (a.createdDate ?? '').localeCompare(b.createdDate ?? ''));
}

/** The review stages the card can show, mirroring common-lib's `TaskReviewStage`. */
export type AgentAtWorkVerdictStage = 'submitted' | 'approved' | 'changesRequested' | 'failed';

/**
 * The verdict badge's label key and DS variant.
 *
 * The variant comes from the same status vocabulary every other badge uses — an approval reads like Done,
 * a change request like Action needed, a rejection like Failed. A verdict is not a new colour language,
 * and both phones map the same four stages onto their own equivalents of these chips.
 */
export const VERDICT_BADGE: Record<AgentAtWorkVerdictStage, { labelKey: string; variant: 'done' | 'waiting-on-you' | 'failed' | 'in-progress' }> = {
  approved: { labelKey: 'chat.agentAtWork.verdict.approved', variant: 'done' },
  changesRequested: { labelKey: 'chat.agentAtWork.verdict.changesRequested', variant: 'waiting-on-you' },
  failed: { labelKey: 'chat.agentAtWork.verdict.failed', variant: 'failed' },
  submitted: { labelKey: 'chat.agentAtWork.verdict.submitted', variant: 'in-progress' },
};

/**
 * Whether a row in the card's window carries its own attribution header.
 *
 * Almost always NO — the card names the agent once, above the transcript, and each side shows only that
 * side's own conversation, so a per-turn header repeated the same name three or four times inside a
 * four-row window. The one case that earns a header is a turn by someone OTHER than the side named at the
 * top: a foreign voice, which is exactly what the header cannot tell you. The change-of-agent rule then
 * still applies within such a run, so a stretch of them is labelled once rather than per row.
 */
export function showsCardAttribution(rows: readonly { agentId?: string }[], index: number, sideAgentId?: string): boolean {
  const row = rows[index];
  if (!row?.agentId || row.agentId === sideAgentId) {
    return false;
  }
  return index === 0 || rows[index - 1]?.agentId !== row.agentId;
}


/** One plan step's visual state on the card's checklist: a check (done), a spinner (current) or a plain
 *  dot (upcoming). 1:1 with iOS `AgentAtWorkStepState` and Android `ChecklistState`. */
export type AgentAtWorkStepState = 'done' | 'current' | 'upcoming';

/**
 * The state of plan step `index` on the card.
 *
 * - **A COMPLETED task ticks every step.** The backend does not always flag the trailing steps of a task
 *   that finished, and once the work is accepted there is nothing left to reach for.
 * - **A task that ended any OTHER way (FAILED, CANCELLED, MOOT) ticks only what the server marks
 *   `completed`**; the rest keep the open dot. Ticking them all made a failed run read as finished work
 *   (Yaya's "Build the Paws And Claws website": "Failed" over six ticked steps, every one
 *   `completed:false` on the server, 2026-10-29).
 * - **A running task**: everything before the first not-done step is done, that step is current, the rest
 *   are upcoming.
 *
 * Same rule on iOS (`AgentAtWorkStepState.of`) and Android (`planStepState`).
 */
export function planStepState(
  index: number,
  stepCompleted: boolean,
  currentIndex: number | null,
  terminal: boolean,
  taskCompleted: boolean,
): AgentAtWorkStepState {
  if (terminal) {
    if (taskCompleted) return 'done';
    return stepCompleted ? 'done' : 'upcoming';
  }
  if (currentIndex === null || index < currentIndex) return 'done';
  if (index === currentIndex) return 'current';
  return 'upcoming';
}
